import React, { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import api from "@/lib/api";
import { devError } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Sparkles, Loader2, RefreshCcw, CalendarClock } from "lucide-react";
import { toast } from "sonner";

function generateButtonLabel(generating, current) {
  if (generating) return "Analyzing operations...";
  return current ? "Regenerate Report" : "Generate Report";
}

function md2html(md) {
  if (!md) return "";
  // simple markdown -> html (## heading, lists, bold)
  const lines = md.split("\n");
  let html = "";
  let inList = false;
  for (const raw of lines) {
    const line = raw.trim();
    if (line.startsWith("## ")) {
      if (inList) { html += "</ul>"; inList = false; }
      html += `<h2>${escapeHtml(line.slice(3))}</h2>`;
    } else if (line.startsWith("# ")) {
      if (inList) { html += "</ul>"; inList = false; }
      html += `<h2>${escapeHtml(line.slice(2))}</h2>`;
    } else if (line.startsWith("- ") || line.startsWith("* ")) {
      if (!inList) { html += "<ul>"; inList = true; }
      html += `<li>${formatInline(line.slice(2))}</li>`;
    } else if (line === "") {
      if (inList) { html += "</ul>"; inList = false; }
    } else {
      if (inList) { html += "</ul>"; inList = false; }
      html += `<p>${formatInline(line)}</p>`;
    }
  }
  if (inList) html += "</ul>";
  return html;
}

function escapeHtml(s) {
  return s.replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);
}
function formatInline(s) {
  return escapeHtml(s).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
}

export default function AIReportPage() {
  const [generating, setGenerating] = useState(false);
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/ai/reports");
      setHistory(data.reports || []);
      if ((data.reports || []).length > 0) setCurrent(data.reports[0]);
    } catch (err) {
      devError("Failed to load AI report history", err);
    }
  };

  useEffect(() => { loadHistory(); }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      // Kick off background job
      const { data: kick } = await api.post("/ai/report/generate");
      const jobId = kick.job_id;
      toast.message("Analyzing 18 modules... this takes 30-90s.", { duration: 3000 });

      // Poll every 3 seconds, up to 3 minutes
      const started = Date.now();
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((r) => setTimeout(r, 3000));
        try {
          const { data: status } = await api.get(`/ai/report/status/${jobId}`);
          if (status.status === "done") {
            setCurrent({ ...status.result, generated_by: "you" });
            toast.success("AI report ready");
            loadHistory();
            break;
          }
          if (status.status === "failed") {
            toast.error("Report generation failed: " + (status.error || "unknown"));
            break;
          }
          if (Date.now() - started > 3 * 60 * 1000) {
            toast.error("Report is taking longer than expected. Check back in a minute — it will appear in Recent Reports.");
            loadHistory();
            break;
          }
        } catch (pollErr) {
          // Transient — keep polling
          if (Date.now() - started > 3 * 60 * 1000) break;
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="ai-report-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">AI Brain</div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mt-1">Weekly Owner Report</h1>
          <p className="text-sm text-slate-500 mt-1">An analytical review of all 18 modules — delays, risks, and recommended actions.</p>
        </div>
        <Button onClick={generate} disabled={generating} className="bg-slate-900 hover:bg-slate-800" data-testid="ai-generate-button">
          {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2 text-amber-300" />}
          {generateButtonLabel(generating, current)}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-3">
          <Card className="border-slate-200">
            <CardContent className="p-0">
              <div className="bg-slate-900 text-white px-6 py-5 rounded-t-lg">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-slate-400 font-semibold">
                  <Sparkles className="h-3.5 w-3.5 text-amber-300" /> Claude Sonnet 4.6 · AI Analyst
                </div>
                <div className="text-lg font-semibold text-white mt-1">Weekly Owner Report</div>
                {current?.generated_at && (
                  <div className="flex items-center gap-1.5 text-xs text-slate-400 mt-2">
                    <CalendarClock className="h-3.5 w-3.5" /> {new Date(current.generated_at).toLocaleString()}
                  </div>
                )}
              </div>
              <div className="p-6">
                {!current && !generating && (
                  <div className="text-center py-16">
                    <Sparkles className="h-10 w-10 text-slate-300 mx-auto" />
                    <div className="mt-4 text-sm text-slate-500">No reports yet. Click <strong>Generate Report</strong> to start.</div>
                  </div>
                )}
                {generating && (
                  <div className="text-center py-16">
                    <Loader2 className="h-10 w-10 text-slate-400 mx-auto animate-spin" />
                    <div className="mt-4 text-sm text-slate-500">Analyzing 18 modules… this may take 20–40 seconds.</div>
                  </div>
                )}
                {current && (
                  <div
                    className="hm-report"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(md2html(current.report_markdown), {
                        ALLOWED_TAGS: ["h1", "h2", "h3", "h4", "p", "ul", "ol", "li", "strong", "em", "br", "code", "hr"],
                        ALLOWED_ATTR: [],
                      }),
                    }}
                    data-testid="ai-report-content"
                  />
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        <div>
          <Card className="border-slate-200">
            <CardContent className="p-5">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Recent Reports</div>
              <div className="space-y-2">
                {history.length === 0 && <div className="text-xs text-slate-500">No previous reports.</div>}
                {history.map((r) => (
                  <button
                    key={r._id}
                    onClick={() => setCurrent(r)}
                    className={`block w-full text-left p-3 rounded-md border transition-colors ${current?._id === r._id ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:bg-slate-50"}`}
                    data-testid={`history-report-${r._id}`}
                  >
                    <div className="text-xs font-semibold text-slate-900">{new Date(r.generated_at).toLocaleString()}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">{r.generated_by}</div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
